<template>
  <component
    :is="isLink ? 'a' : 'button'"
    v-bind="elementAttrs"
    :class="rootClass"
    @click="handleClick"
  >
    <!-- Spinner overlay is always present; sf-button.scss shows it (and hides
         the rest) only while aria-busy, so the button keeps its width when
         loading. -->
    <span class="sf-button__spinner">
      <i class="fa fa-spinner fa-spin" aria-hidden="true" />
    </span>
    <i
      v-if="iconBefore"
      class="fa"
      :class="`fa-${iconBefore}`"
      aria-hidden="true"
    />
    <span v-if="!iconOnly" class="sf-button__label"><slot /></span>
    <!-- No margin here: the button's own per-size gap spaces the icon, as it
         does for iconBefore. tw-shrink-0 keeps it whole next to a label that
         truncates. -->
    <i
      v-if="iconAfter"
      class="fa tw-shrink-0"
      :class="`fa-${iconAfter}`"
      aria-hidden="true"
    />
  </component>
</template>

<script setup lang="ts">
import { computed, useAttrs, watchEffect } from 'vue'
import { SfLog } from '@app/adapters/logger'
import env from '@app/utils/environment'

// Light-DOM design-system button — the single Vue implementation, used wherever
// the application stylesheet is loaded (e.g. the report graph editor). Its BEM
// classes and Font Awesome <i> markup match the Rails Buttons::SfButtonComponent
// exactly; the parity system spec pins the two together.

defineOptions({ name: 'SfButtonDs' })

const ICON_ONLY_CLASS = 'sf-button--icon-only'

// Mirrors Buttons::SfButtonComponent VARIANTS/SIZES (the Rails source of truth).
type Variant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'ai'
type Size = 'sm' | 'md' | 'lg'

interface Props {
  variant?: Variant
  size?: Size
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  iconOnly?: boolean
  iconBefore?: string
  iconAfter?: string
  href?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'secondary',
  size: 'lg',
  type: 'button',
  disabled: false,
  loading: false,
  iconOnly: false,
  iconBefore: undefined,
  iconAfter: undefined,
  href: undefined,
})

const isLink = computed(() => Boolean(props.href))
const inactive = computed(() => props.disabled || props.loading)

// Dev-only mirror of the Rails component's icon_only accessible-name guard: an
// icon-only button has no visible label, so it needs a name from aria-label,
// aria-labelledby, or title (all arrive as fall-through attrs, not props). The
// Rails component adds an `aria_label:` shortcut because its equivalent is
// nested (html: { aria: { label: } }); here `:aria-label="…"` is already the
// idiomatic form, so no matching prop is needed.
if (env.__DEV__) {
  const attrs = useAttrs()
  watchEffect(() => {
    // A caller whose icon is a Vue component passes sf-button--icon-only as a
    // class, because the prop hides the slot that icon lives in.
    const iconOnly =
      props.iconOnly || String(attrs.class ?? '').includes(ICON_ONLY_CLASS)
    if (
      iconOnly &&
      !attrs['aria-label'] &&
      !attrs['aria-labelledby'] &&
      !attrs.title
    ) {
      SfLog.warn(
        '[SfButtonDs] icon-only button needs an accessible name: pass aria-label, aria-labelledby, or title'
      )
    }
  })
}

// <a> and <button> take different attributes, so build the right set per tag
// instead of scattering isLink checks across the template. A disabled link
// keeps its href (stays focusable/announced) and uses aria-disabled; a disabled
// button uses the native disabled attribute.
const elementAttrs = computed(() =>
  isLink.value
    ? {
        href: props.href,
        'aria-disabled': inactive.value ? 'true' : undefined,
        'aria-busy': props.loading ? 'true' : undefined,
      }
    : {
        type: props.type,
        disabled: inactive.value,
        'aria-busy': props.loading ? 'true' : undefined,
      }
)

const rootClass = computed(() => [
  'sf-button',
  `sf-button--${props.variant}`,
  `sf-button--${props.size}`,
  { [ICON_ONLY_CLASS]: props.iconOnly },
])

// A disabled <a> still navigates on Enter even with pointer-events: none, so
// block activation here; the native disabled attribute already covers <button>.
function handleClick(event: Event): void {
  if (isLink.value && inactive.value) {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../scss/components/sf-button';
</style>
